import { sanitiser } from './sanitiser';
import { useQuery } from '@tanstack/react-query';
import type { TypeName } from '@/components/Modules';
import { findDeepObjectsByKey } from '@/utils/helpers';
import { createContext, useState, useMemo } from 'react';
import type { ProviderProps, CalcContext } from './types';
import { getPricingData } from '@/utils/queries/getPricingData';
import type { CalculatorFragment } from '@/utils/dato/generated';
import { getChannel, getMaximum, getMinimum, getAmount, getTerm } from './defaults';

/**
 * Context for the Calculator
 */
export const CalculatorContext = createContext<CalcContext>({
  setSelectedTerm: () => undefined,
  setAmount: () => undefined,
  selectedTerm: getTerm(),
  channel: getChannel(),
  maximum: getMaximum(),
  minimum: getMinimum(),
  amount: getAmount(),
  termInformation: [],
});

/**
 * Context Provider for the Calculator
 *
 * @param param0 ProviderProps<T>
 * @returns JSX
 */
export function CalculatorProvider<T extends TypeName>({
  data: { defaultSelectedTerm, defaultAmount, channel, maximum, minimum, terms },
  children,
  modules,
}: ProviderProps<T>) {
  const [selectedTerm, setSelectedTerm] = useState(defaultSelectedTerm);
  const [amount, setAmount] = useState(sanitiser(defaultAmount, minimum, maximum));

  /**
   * Check if page contains the calculator block
   */
  const calculators = findDeepObjectsByKey<CalculatorFragment>(modules, '__typename', 'CalculatorRecord');

  /**
   * Pricing Query
   */
  const { error, data } = useQuery({
    queryFn: () =>
      getPricingData({
        channel: channel,
      }),
    queryKey: ['calcualtor', channel],
    enabled: !!calculators.length,
  });

  /**
   * Get the terms and their information
   */
  const termInformation = useMemo(() => {
    // This will be undefined if there aren't any calculator modules in the page
    const pricingData = data?.data.pricingData;

    if (!pricingData) {
      return [];
    }

    const value = sanitiser(amount, minimum, maximum);

    return terms
      .filter((term) => typeof pricingData.pricing[`ID_${term}_${value}`] !== 'undefined')
      .map((term) => ({
        value: pricingData.pricing[`ID_${term}_${value}`].min_pmt,
        apr: pricingData.pricing[`ID_${term}_${value}`].min_apr,
        years: Math.ceil(term / 12),
        term,
      }));
  }, [amount, terms, data, minimum, maximum]);

  if (!calculators.length) {
    /**
     * There aren't any calculators in the page
     * so we don't need to wrap in context
     */
    return <>{children}</>;
  }

  return (
    <CalculatorContext.Provider
      value={{
        setAmount: (value, sanitise) => {
          if (Number.isNaN(Number(value))) {
            /** Do not update the amount if it's not a number */
            return;
          }

          const amount = sanitise ? sanitiser(value, minimum, maximum) : value;

          setAmount(amount);
        },
        pricing: data?.data,
        setSelectedTerm,
        termInformation,
        selectedTerm,
        channel,
        maximum,
        minimum,
        amount,
        error,
      }}
    >
      {children}
    </CalculatorContext.Provider>
  );
}
